// src/features/faq/components/BusinessFAQs.tsx

import React from 'react';
import { 
  Building, 
  PaintBucket, 
  Clock, 
  Coins, 
  Shield, 
  Sliders, 
  Crown, 
  ArrowRight,
  Edit,
  ToggleLeft,
  ToggleRight,
  CreditCard,
  ScanLine,
  Smartphone
} from 'lucide-react';

export interface BusinessFAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface BusinessFAQData {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  faqs: BusinessFAQItem[];
}

export const BusinessFAQs = (navigateCallback: (path: string) => void): BusinessFAQData => {

  return {
    id: 'business',
    title: 'Mi Negocio',
    icon: <Building className="h-5 w-5 text-accent" />,
    description: 'Todo sobre la configuración y personalización de tu negocio en la plataforma.',
    faqs: [
      {
        id: 'business-tabs',
        question: '¿Cómo está organizada la página "Mi Negocio"?',
        answer: (
          <div>
            <p>La página de <button onClick={() => navigateCallback('/dashboard/business')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button> está organizada en cinco pestañas, cada una responde una pregunta:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Identidad</strong> (¿quién soy?): nombre del negocio, logotipo, colores, zona horaria y sucursales.</li>
              <li><strong>Cómo premio</strong> (¿qué gana mi cliente?): modelo de lealtad (puntos por visita o cashback), porcentajes, días con premio extra (martes x1.5, Buen Fin x2…) y puntos dobles por cumpleaños.</li>
              <li><strong>Cómo registro visitas</strong> (¿cómo cuento la visita?): kiosco, QR en el mostrador, tarjeta QR del cliente, monto y folio de la compra, quién no acumula.</li>
              <li><strong>Kiosco</strong> (¿qué ve mi cliente?): los datos que pide el registro, la lada del teléfono y los términos que acepta.</li>
              <li><strong>Promoción</strong> (¿cómo lo doy a conocer?): códigos QR hacia tu sitio o redes.</li>
            </ul>
            <p className="mt-2 text-content-2">El corte diario automático ya no está aquí: vive en Análisis → Corte diario.</p>
            <p className="mt-2 text-content-2">Cuando hagas cambios en cualquier pestaña, guárdalos con la barra de guardado que aparece en la parte inferior.</p>
          </div>
        )
      },
      {
        id: 'business-name',
        question: '¿Cómo cambio el nombre de mi negocio?',
        answer: (
          <div>
            <p>Para cambiar el nombre de tu negocio que se muestra en la plataforma:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Ve a la sección <button onClick={() => navigateCallback('/dashboard/business')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button> en el menú lateral.</li>
              <li>En la parte superior del formulario, edita el campo "Nombre del Negocio".</li>
              <li>Asegúrate de desplazarte hasta abajo y hacer clic en el botón "Guardar Cambios".</li>
            </ol>
            <p className="mt-2 text-content-2">El nombre de tu negocio es fundamental, ya que aparecerá en los paneles de visita de tus clientes, en la app de clientes (si aplica) y en las comunicaciones automáticas.</p>
            <div className="mt-2 bg-blue-50 dark:bg-blue-500/10 p-3 rounded-md border border-blue-200 dark:border-blue-500/30">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Consejo:</strong> Elige un nombre claro, conciso y fácilmente reconocible para tu negocio. Esto ayuda a tus clientes a identificarte rápidamente en el panel de visitas y fortalece tu marca.
              </p>
            </div>
          </div>
        )
      },
      {
        id: 'business-branches',
        question: '¿Cómo configuro las sucursales de mi negocio?',
        answer: (
          <div>
            <p>Puedes gestionar las sucursales de tu negocio de la siguiente manera:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Accede a <button onClick={() => navigateCallback('/dashboard/business')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button> desde el menú lateral.</li>
              <li>En la sección "Sucursales", puedes hacer clic en "Añadir Sucursal" para crear una nueva. Ingresa su nombre y guarda.</li>
              <li>Para sucursales existentes, puedes:</li>
              <ul className="list-disc ml-8 mt-1">
                <li>Editar el nombre haciendo clic en el icono de lápiz (<Edit className="inline h-3 w-3 text-content-2" />).</li>
                <li>Activar o desactivar una sucursal usando el interruptor (<ToggleRight className="inline h-3 w-3 text-green-500" /> / <ToggleLeft className="inline h-3 w-3 text-content-3" />).</li>
              </ul>
            </ol>
            <div className="mt-2 bg-amber-50 dark:bg-amber-500/10 p-3 rounded-md border border-amber-200 dark:border-amber-500/30">
              <p className="text-sm text-amber-700">
                <strong className="flex items-center"><Crown className="h-4 w-4 mr-1.5 text-yellow-600 dark:text-yellow-400" />Sucursales adicionales:</strong> Todos los planes incluyen <strong>1 sucursal</strong>. ¿Tienes más locales? El precio por sucursal <strong>baja por volumen</strong>: 1ª-3ª a $2,500/año c/u, 4ª-6ª a $2,000, 7ª+ a $1,500 (entre más, más barata cada una; no se multiplica plano). Escríbenos y te las activamos, o
                <button onClick={() => navigateCallback('/dashboard/subscription')} className="ml-1 text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:text-pink-300 font-medium inline-flex items-center">
                  revisa tu suscripción <ArrowRight className="h-3 w-3 ml-1" />
                </button>.
              </p>
            </div>
            <p className="mt-2 text-content-2">Las sucursales te permiten organizar tus operaciones por ubicación, registrar visitas por separado y obtener estadísticas específicas para cada punto de venta.</p>
          </div>
        )
      },
      {
        id: 'business-colors',
        question: '¿Cómo personalizo los colores de mi negocio?',
        answer: (
          <div>
            <p>Para personalizar los colores que representan tu marca en la plataforma:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Desde <button onClick={() => navigateCallback('/dashboard/business')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button>, localiza la sección "Colores del Negocio".</li>
              <li>Podrás modificar:
                <ul className="list-disc ml-8 mt-1">
                  <li><div className="flex items-center"><PaintBucket className="h-4 w-4 mr-1 text-blue-500" /><strong>Color Principal:</strong></div> Se utiliza para elementos destacados como encabezados, botones principales y acentos visuales.</li>
                  <li><div className="flex items-center"><PaintBucket className="h-4 w-4 mr-1 text-content-3" /><strong>Color Secundario:</strong></div> Se usa para fondos, bordes y otros elementos complementarios.</li>
                </ul>
              </li>
              <li>Haz clic en cada selector de color para elegir los tonos que desees. Verás una previsualización en tiempo real.</li>
              <li>No olvides guardar los cambios al final de la página.</li>
            </ol>
            <div className="mt-2 bg-blue-50 dark:bg-blue-500/10 p-3 rounded-md border border-blue-200 dark:border-blue-500/30">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Consejo de diseño:</strong> Elige colores que reflejen la identidad de tu marca y aseguren una buena legibilidad. Un contraste adecuado entre el color principal y el texto es importante. Considera usar los colores de tu logotipo o de tu local físico para mantener la coherencia.
              </p>
            </div>
            <p className="mt-2 text-content-2">Esta personalización se reflejará en el panel de visitas que ven tus clientes y en otras áreas del sistema.</p>
          </div>
        )
      },
      {
        id: 'business-timezone',
        question: '¿Cómo configuro la zona horaria de mi negocio?',
        answer: (
          <div>
            <p>Configurar correctamente la zona horaria es crucial para la precisión de los registros:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>En <button onClick={() => navigateCallback('/dashboard/business')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button>, busca la sección "Zona Horaria".</li>
              <li>Selecciona en el desplegable la zona horaria que corresponda a la ubicación principal de tu negocio.</li>
              <li>El sistema te mostrará la hora actual en la zona seleccionada para ayudarte a confirmar. También te indicará si es diferente a la zona horaria de tu navegador.</li>
              <li>Guarda los cambios.</li>
            </ol>
            <div className="flex items-start mt-3 p-3 bg-surface-2 rounded-lg border border-line">
              <Clock className="h-6 w-6 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-content">Importancia de la Zona Horaria</h4>
                <p className="mt-1 text-sm text-content-2">Una zona horaria correcta asegura que:</p>
                <ul className="list-disc ml-4 mt-1 text-sm text-content-2 space-y-0.5">
                  <li>Los registros de visitas tengan la fecha y hora exactas.</li>
                  <li>Los reportes diarios y las estadísticas se generen correctamente.</li>
                  <li>Los intervalos de tiempo para la acumulación de puntos se calculen bien.</li>
                  <li>Los cumpleaños se identifiquen en el día correcto para las felicitaciones.</li>
                </ul>
              </div>
            </div>
            <p className="mt-2 text-content-2">Si tienes múltiples sucursales en diferentes zonas horarias, selecciona la zona horaria de tu sede principal o la más representativa para las operaciones generales del sistema.</p>
          </div>
        )
      },
      {
        id: 'business-points',
        question: '¿Cómo configuro el sistema de puntos por visita?',
        answer: (
          <div>
            <p>Puedes definir cómo tus clientes ganan y acumulan puntos por sus visitas:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Dirígete a <button onClick={() => navigateCallback('/dashboard/business?tab=rewards')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button>, pestaña <strong>Cómo premio</strong>.</li>
              <li>Elige el modelo <strong>Puntos por Visita</strong> y localiza la sección "Configuración de puntos y visitas". <span className="text-content-2">(Si prefieres el modelo <strong>Cashback</strong>, en su lugar configurarás un porcentaje de retorno en dinero y no se piden puntos por visita.)</span></li>
              <li>Aquí podrás ajustar:
                <ul className="list-disc ml-8 mt-1 space-y-1">
                  <li><div className="flex items-center"><Coins className="h-4 w-4 mr-1 text-yellow-500" /><strong>Puntos por visita:</strong></div> Define cuántos puntos otorga cada visita registrada (mínimo 1).</li>
                  <li><div className="flex items-center"><Clock className="h-4 w-4 mr-1 text-blue-500" /><strong>Tiempo entre visitas (minutos):</strong></div> Establece un intervalo mínimo (ej. 60 minutos) para que una nueva visita del mismo cliente genere puntos. Esto previene abusos o registros duplicados accidentales.</li>
                  <li><div className="flex items-center"><Sliders className="h-4 w-4 mr-1 text-green-500" /><strong>Permitir acumulación de puntos:</strong></div> Activa o desactiva globalmente si los clientes pueden o no acumular puntos con sus visitas.</li>
                </ul>
              </li>
              <li>Guarda los cambios.</li>
            </ol>
            <div className="mt-4 bg-green-50 dark:bg-green-500/10 p-4 rounded-lg border border-green-200 dark:border-green-500/30">
              <h4 className="font-medium text-green-800 dark:text-green-300 flex items-center mb-2">
                <Shield className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
                Estrategias Recomendadas para el Sistema de Puntos
              </h4>
              <ul className="list-disc ml-4 space-y-2 text-sm text-green-700 dark:text-green-300">
                <li><strong>Negocios de consumo frecuente (cafeterías, comida rápida):</strong> 1-5 puntos por visita, tiempo mínimo entre visitas de 1-3 horas.</li>
                <li><strong>Tiendas minoristas, servicios personales:</strong> 5-10 puntos por visita, tiempo mínimo de 6-12 horas.</li>
                <li><strong>Servicios con ciclos más largos (ej. mensual):</strong> 10-25 puntos por visita, tiempo mínimo de 24-48 horas.</li>
                <li><strong>Valor de las Recompensas:</strong> Asegúrate de que tus <button onClick={() => navigateCallback('/dashboard/rewards')} className="text-green-700 dark:text-green-300 hover:text-green-900 font-medium">recompensas</button> requieran un número de visitas/puntos que sea alcanzable pero que también incentive la recurrencia (ej. si das 1 punto por visita, una recompensa podría costar 10 puntos, equivalente a 10 visitas).</li>
              </ul>
            </div>
            <p className="mt-3 text-content-2">Un sistema de puntos bien balanceado es fundamental para el éxito de tu programa de lealtad.</p>
          </div>
        )
      },
      {
        id: 'business-client-data',
        question: '¿Cómo personalizo la información que solicito a los clientes al registrarse?',
        answer: (
          <div>
            <p>Puedes elegir qué datos adicionales (aparte del número de teléfono, que es obligatorio) solicitar a tus clientes durante su primer registro en el panel de visitas:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>En <button onClick={() => navigateCallback('/dashboard/business?tab=kiosk')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button>, pestaña <strong>Kiosco</strong>, busca la sección "Registro de clientes en el kiosco".</li>
              <li>Aquí puedes activar o desactivar la solicitud de los siguientes campos:
                <ul className="list-disc ml-8 mt-1">
                  <li><strong>Género:</strong> Útil para segmentación y estadísticas demográficas.</li>
                  <li><strong>Fecha de cumpleaños:</strong> Imprescindible para usar el Panel de Cumpleaños y enviar felicitaciones.</li>
                  <li><strong>Correo electrónico:</strong> Para canales de comunicación adicionales o campañas de email marketing.</li>
                </ul>
              </li>
              <li>Guarda los cambios.</li>
            </ol>
            <div className="mt-2 bg-blue-50 dark:bg-blue-500/10 p-3 rounded-md border border-blue-200 dark:border-blue-500/30">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Prácticas recomendadas:</strong> Solicita solo la información que realmente planeas utilizar. Un formulario de registro muy largo puede disuadir a algunos clientes. El nombre y el teléfono son esenciales para el sistema; los demás campos son opcionales según tus estrategias.
              </p>
            </div>
            <p className="mt-2 text-content-2">Puedes modificar estas opciones en cualquier momento. Los clientes ya registrados no se verán afectados retroactivamente si desactivas un campo que ya habían llenado.</p>
          </div>
        )
      },
      {
        id: 'business-qr-codes',
        question: '¿Cómo genero y personalizo códigos QR para mi negocio?',
        answer: (
          <div>
            <p>Los códigos QR facilitan que tus clientes interactúen con tu programa de lealtad o tus redes.</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Ve a <button onClick={() => navigateCallback('/dashboard/business?tab=promo')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button>, pestaña <strong>Promoción</strong>.</li>
              <li>Ve a la sección "Códigos QR Para Tu Negocio".</li>
              <li>Encontrarás dos tipos de QR:
                <ul className="list-disc ml-8 mt-1">
                  <li><strong>Código QR Predeterminado (Registro de Visitas):</strong> Dirige a tus clientes a la página de check-in (kiosco) de tu negocio, donde registran su visita y consultan sus puntos.</li>
                  <li><strong>Código QR Personalizado (Redes Sociales/Web):</strong> Puedes configurarlo para que dirija a tus clientes a tu página de Facebook, Instagram, WhatsApp, sitio web, etc.</li>
                </ul>
              </li>
            </ol>
            <p className="mt-2">Para personalizar el código QR de redes sociales/web (disponible para usuarios PRO):</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Haz clic en "Editar" junto al Código QR Personalizado.</li>
              <li>Selecciona la plataforma o tipo de enlace que deseas (ej. Facebook, Instagram, URL propia).</li>
              <li>Ingresa la URL completa de tu perfil, página o sitio web.</li>
              <li>Guarda los cambios y el código QR se actualizará.</li>
            </ol>
            <div className="mt-2 bg-amber-50 dark:bg-amber-500/10 p-3 rounded-md border border-amber-200 dark:border-amber-500/30">
              <p className="text-sm text-amber-700">
                <strong className="flex items-center"><Crown className="h-4 w-4 mr-1.5 text-yellow-600 dark:text-yellow-400" />Función PRO:</strong> La personalización del segundo código QR para dirigir a tus redes sociales o sitio web específicos es una funcionalidad exclusiva del plan PRO. Los usuarios del plan gratuito pueden utilizar el código QR predeterminado para el registro de visitas. Considera 
                <button onClick={() => navigateCallback('/dashboard/subscription')} className="ml-1 text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:text-pink-300 font-medium inline-flex items-center">
                  actualizar a PRO <ArrowRight className="h-3 w-3 ml-1" />
                </button> para esta y otras ventajas.
              </p>
            </div>
            <p className="mt-2 text-content-2">Puedes descargar, imprimir y colocar estos códigos QR en un lugar visible de tu establecimiento.</p>
          </div>
        )
      },
      {
        id: 'business-card-scan',
        question: '¿Qué es la tarjeta QR del cliente y cómo la activo?',
        answer: (
          <div>
            <p>Es una <strong>tarjeta digital con código QR</strong> que tu cliente guarda en la pantalla de inicio de su celular, con el nombre y el icono de tu negocio. No descarga ninguna app: es una página con su QR. Cuando viene, tú la escaneas desde el manager en tu celular y la visita queda registrada al instante. Sirve para registrar visitas <strong>sin tablet ni kiosco</strong>, y es gratis en todos los planes.</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Ve a <button onClick={() => navigateCallback('/dashboard/business?tab=visits')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Mi Negocio</button>, pestaña <strong>Cómo registro visitas</strong>, y enciende <strong>Tarjeta QR del cliente</strong>.</li>
              <li>En el menú lateral aparece <button onClick={() => navigateCallback('/dashboard/card-scan')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Escanear tarjeta</button> (<ScanLine className="inline h-3 w-3 text-content-2" />). Ábrelo desde el celular del empleado que recibe a los clientes.</li>
              <li>Pulsa <strong>Escanear tarjeta</strong>, apunta la cámara al QR del cliente y confirma la visita en la misma ventana de «Registrar visita» (monto, folio y notas, si los pides).</li>
            </ol>
            <p className="mt-2 text-content-2">Puedes combinarla con el kiosco y con el QR del mostrador: las tres formas suman al mismo programa. Las visitas escaneadas quedan con origen «escáner» en el historial del cliente y en Reportes, con el nombre de quien las registró.</p>
          </div>
        )
      },
      {
        id: 'business-card-give',
        question: '¿Cómo le doy su tarjeta a un cliente?',
        answer: (
          <div>
            <p>Hay dos maneras, y las dos llevan a la misma tarjeta:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><div className="flex items-center"><Smartphone className="h-4 w-4 mr-1 text-blue-500" /><strong>Él mismo, en el kiosco:</strong></div> al identificarse con su número en la tablet o en el QR del mostrador, aparece el botón «Mi tarjeta» y la guarda en su celular siguiendo las instrucciones de la pantalla («Agregar a inicio»).</li>
              <li><div className="flex items-center"><CreditCard className="h-4 w-4 mr-1 text-blue-500" /><strong>Tú, desde su historial:</strong></div> abre al cliente en <button onClick={() => navigateCallback('/dashboard/clients')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Lista de clientes</button> y pulsa el botón <strong>Tarjeta</strong>. Desde ahí se la mandas por WhatsApp, copias el enlace o la imprimes con tu logotipo y colores.</li>
            </ul>
            <p className="mt-2 text-content-2">Cada cliente tiene un código único que se crea la primera vez que abres su tarjeta. El botón «Tarjeta» solo aparece cuando la función está encendida en Mi Negocio.</p>
          </div>
        )
      },
      {
        id: 'business-card-fallback',
        question: '¿Qué pasa si el cliente no trae su tarjeta o la cámara no lee el QR?',
        answer: (
          <div>
            <p>La tarjeta es un atajo, no un requisito. Siempre tienes estas salidas:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>No trae la tarjeta:</strong> búscalo por nombre o teléfono en Lista de clientes y registra la visita desde su historial, como siempre.</li>
              <li><strong>La cámara no lee o no tienes cámara:</strong> en el escáner pulsa <strong>«¿No lee? Escribe el código»</strong> y teclea el código que viene impreso debajo del QR de su tarjeta (entre 6 y 12 letras y números; no usa O, 0, I ni 1 para evitar confusiones).</li>
              <li><strong>El navegador no pide permiso de cámara:</strong> permite la cámara para este sitio en los ajustes del navegador, o abre el manager desde el celular; en computadora sin cámara usa el código a mano.</li>
            </ul>
          </div>
        )
      },
    ]
  };
};